import { useEffect, useRef, useState } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Drawer from '@mui/material/Drawer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CategoryRail } from '../catalog/CategoryRail';
import { readCatalogParams, withParams, type DetailTab } from '../catalog/catalogParams';
import { FormDetailPane } from '../catalog/FormDetailPane';
import { FormList } from '../catalog/FormList';
import { SearchField } from '../catalog/SearchField';
import { useCategories } from '../catalog/useCategories';
import { useDebouncedValue } from '../catalog/useDebouncedValue';
import { useFormDetail } from '../catalog/useFormDetail';
import { useFormSearch } from '../catalog/useFormSearch';
import { COPY } from '../copy';
import { PageHeading } from '../shell/PageHeading';
import { useIsDesktop } from '../shell/useIsDesktop';
import { usePageTitle } from '../shell/usePageTitle';

export function StartProcessPage() {
  usePageTitle(COPY.navStart);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const p = readCatalogParams(sp);
  const [input, setInput] = useState(p.q);
  const [trackedQ, setTrackedQ] = useState(p.q);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const previousFormId = useRef(p.formId);

  if (trackedQ !== p.q) {
    setTrackedQ(p.q);
    setInput(p.q);
  }

  const debounced = useDebouncedValue(input, 150);
  const debouncedRef = useRef(debounced);

  useEffect(() => {
    if (!p.invalid) return;
    setSp(
      withParams(new URLSearchParams(), {
        q: p.q || null,
        category: p.categoryId,
        form: p.formId,
        tab: p.tab === 'form' ? 'form' : null,
        page: p.page,
      }),
      { replace: true },
    );
  }, [p.invalid, p.q, p.categoryId, p.formId, p.tab, p.page, setSp]);

  useEffect(() => {
    const previous = debouncedRef.current;
    debouncedRef.current = debounced;
    if (previous === debounced || debounced === p.q) return;
    setSp(withParams(sp, { q: debounced || null }), { replace: true });
  }, [debounced, p.q, sp, setSp]);

  const categories = useCategories();
  const search = useFormSearch(p.q, p.categoryId);
  const detail = useFormDetail(p.formId);
  const scopeName =
    p.categoryId === null
      ? COPY.allForms
      : (categories.items.find((category) => category.id === p.categoryId)?.name ??
        COPY.selectedCategory);

  useEffect(() => {
    const previous = previousFormId.current;
    previousFormId.current = p.formId;
    if (!isDesktop && previous !== null && p.formId === null) {
      listRef.current?.querySelector<HTMLElement>('h1')?.focus();
    }
  }, [isDesktop, p.formId]);

  const onClearSearch = () => {
    setInput('');
    setSp(withParams(sp, { q: null }), { replace: true });
  };
  const onShowAll = () => {
    setSp(withParams(sp, { category: null }));
  };
  const onTab = (tab: DetailTab) => {
    setSp(withParams(sp, { tab: tab === 'form' ? 'form' : null, page: null }), { replace: true });
  };
  const onPage = (n: number) => {
    const next = n > 1 ? n : null;
    if (next === p.page) return;
    setSp(withParams(sp, { page: next }), { replace: true });
  };
  const onNotFoundBack = () => {
    setSp(withParams(sp, { form: null, tab: null, page: null }), { replace: true });
  };
  const back = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else setSp(withParams(sp, { form: null, tab: null, page: null }));
  };

  const list = (
    <FormList
      search={search}
      selectedId={p.formId}
      q={p.q}
      categoryId={p.categoryId}
      scopeName={scopeName}
      onClearSearch={onClearSearch}
      onShowAll={onShowAll}
    />
  );
  const details = (
    <FormDetailPane
      detail={detail}
      tab={p.tab}
      headingLevel={isDesktop ? 'h2' : 'h1'}
      focusOnShow={!isDesktop}
      onTab={onTab}
      page={p.page}
      onPage={onPage}
      onNotFoundBack={onNotFoundBack}
    />
  );

  if (isDesktop) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box sx={{ px: 3, pt: 2 }}>
          <PageHeading>{COPY.navStart}</PageHeading>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1.5, px: 3, pb: 3 }}>
          <Card
            variant="outlined"
            component="section"
            aria-labelledby="categories-heading"
            sx={{
              width: (theme) => theme.spacing(32),
              flexShrink: 0,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <CategoryRail categories={categories} selectedId={p.categoryId} />
          </Card>
          <Card
            variant="outlined"
            component="section"
            aria-label={COPY.formsListLabel}
            sx={{
              width: (theme) => theme.spacing(50),
              flexShrink: 0,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <Box sx={{ p: 2, pb: 0 }}>
              <SearchField value={input} onChange={setInput} />
            </Box>
            {list}
          </Card>
          <Card
            variant="outlined"
            component="section"
            aria-label={COPY.formDetailsLabel}
            sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}
          >
            {details}
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box ref={listRef} hidden={p.formId !== null} sx={{ px: 2, pt: 2 }}>
        <PageHeading>{COPY.navStart}</PageHeading>
        <SearchField value={input} onChange={setInput} />
        <Button
          variant="outlined"
          fullWidth
          sx={{ mt: 1.5, justifyContent: 'space-between' }}
          endIcon={<ExpandMore />}
          onClick={() => setDrawerOpen(true)}
          aria-haspopup="dialog"
        >
          {COPY.categoryButton(scopeName)}
        </Button>
        <Box sx={{ mx: -2, mt: 1 }}>{list}</Box>
      </Box>
      {p.formId !== null && (
        <Box>
          <Box sx={{ px: 1, pt: 1 }}>
            <Button startIcon={<ArrowBack />} onClick={back}>
              {COPY.backToForms}
            </Button>
          </Box>
          {details}
        </Box>
      )}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        aria-labelledby="categories-heading"
        slotProps={{
          paper: { sx: { width: (theme) => theme.spacing(36), maxWidth: '85vw' } },
        }}
      >
        <CategoryRail
          categories={categories}
          selectedId={p.categoryId}
          onNavigate={() => setDrawerOpen(false)}
        />
      </Drawer>
    </Box>
  );
}
